import { Injectable, ElementRef, NgZone } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { TVNavigationService, Region } from './tv-navigation.service';
import { DeviceService } from './tv-detect.service';

/**
 * VideoPlayerTVController - Coordinates video player focus trap with TV navigation
 *
 * This service:
 * - Manages focus trap mode for player controls
 * - Provides interaction signals for auto-hide timer reset
 * - Handles hierarchical Back button (menus -> controls -> exit)
 * - Disables navigation during ad playback
 */
@Injectable({ providedIn: 'root' })
export class VideoPlayerTVController {
  /** Whether focus is currently trapped in player */
  private isTrapped = false;

  /** Whether navigation is disabled (during ads) */
  private isNavigationDisabled = false;

  /** Current player region ID */
  private currentRegionId: string | null = null;

  /** Subscription to navigation service interaction events */
  private interactionSub: Subscription | null = null;

  /** Forward player interaction events from navigation service */
  public playerInteraction$ = this.navService.playerInteraction$;

  constructor(
    private navService: TVNavigationService,
    private deviceService: DeviceService,
    private zone: NgZone
  ) {}

  /**
   * Check if running on TV platform
   */
  isTV(): boolean {
    return this.deviceService.isTV();
  }

  /**
   * Enter focus trap mode - register player controls as form region
   * @param regionId Unique region ID (e.g., 'player-controls', 'player-episodes')
   * @param containerRef Container element reference
   * @param focusableItems Array of focusable button references
   * @param initialIndex Initial focus index (default 0)
   */
  enterTrapMode(
    regionId: string,
    containerRef: ElementRef,
    focusableItems: ElementRef[],
    initialIndex: number = 0
  ): void {
    if (!this.deviceService.isTV()) return;

    // Exit existing trap if any
    if (this.isTrapped && this.currentRegionId) {
      this.navService.unregisterRegion(this.currentRegionId);
    }

    // Register controls as form region (vertical navigation only)
    const region: Region = {
      id: regionId,
      type: 'form',
      elementRef: containerRef,
      focusableItems: focusableItems
    };

    this.navService.registerRegion(region);
    this.navService.activateRegion(regionId, initialIndex);

    this.currentRegionId = regionId;
    this.isTrapped = true;

    console.log(`[TV Player] Entered trap mode: ${regionId}`);
  }

  /**
   * Exit focus trap mode - unregister player region
   */
  exitTrapMode(): void {
    if (!this.isTrapped || !this.currentRegionId) return;

    this.navService.unregisterRegion(this.currentRegionId);
    this.currentRegionId = null;
    this.isTrapped = false;

    console.log('[TV Player] Exited trap mode');
  }

  /**
   * Check if currently in trap mode
   */
  isInTrapMode(): boolean {
    return this.isTrapped;
  }

  /**
   * Get current trap region ID
   */
  getCurrentRegionId(): string | null {
    return this.currentRegionId;
  }

  /**
   * Disable all D-pad navigation (during ad playback)
   */
  disableNavigation(): void {
    this.isNavigationDisabled = true;
    if (this.isTrapped) {
      this.exitTrapMode();
    }
    console.log('[TV Player] Navigation disabled (ad playing)');
  }

  /**
   * Re-enable D-pad navigation (after ads complete)
   */
  enableNavigation(): void {
    this.isNavigationDisabled = false;
    console.log('[TV Player] Navigation enabled');
  }

  /**
   * Check if navigation is currently disabled
   */
  isNavigationEnabled(): boolean {
    return !this.isNavigationDisabled;
  }

  /**
   * Update focusable items in current trap (for dynamic content)
   * @param focusableItems Updated array of focusable references
   */
  updateFocusableItems(focusableItems: ElementRef[]): void {
    if (!this.isTrapped || !this.currentRegionId) return;

    // Re-register with updated items
    const currentIndex = this.navService.getFocusedIndex();
    const currentRegion = (this.navService as any).regions?.get(this.currentRegionId);
    const containerRef = currentRegion?.elementRef;

    if (containerRef) {
      const region: Region = {
        id: this.currentRegionId,
        type: 'form',
        elementRef: containerRef,
        focusableItems: focusableItems
      };

      this.navService.unregisterRegion(this.currentRegionId);
      this.navService.registerRegion(region);
      this.navService.activateRegion(this.currentRegionId, Math.min(currentIndex, focusableItems.length - 1));
    }
  }
}
